// QR Kiosk v2 — §8.4 single source of truth for resolving a page/schema artifact
// to its FETCHABLE image URL. Schema/diagram pages (a `diagram_overlay` artifact,
// or a `cross_reference` list-ref) are served from the dedicated page-image
// endpoint keyed by PAGE NUMBER — `${apiBaseUrl}/v1/bot/diagram/{page}/image` — NOT
// the generic `/v1/bot/image/{path}` proxy. This mirrors desktop exactly:
//   - MidasArtifactThumbnail.getImageUrlToFetch (diagram_overlay → /v1/bot/diagram/{page}/image)
//   - MidasArtifactDiagramViewerV2 (fetches the diagram endpoint by page)
//   - ChatBotMessageV3 source-card mapper (builds the absolute diagram URL up-front)
// The bot's `data.image_url` on a diagram_overlay is a bot-internal reference the
// image proxy cannot serve (desktop never uses it) — feeding it to fetchBotImage
// was why the kiosk viewer + card thumbnail failed to load. Pure TS, MUI-free.
import { ChatArtifact } from "../../../shared/chatBot/models/chatBotModels";

/** Numeric page for a page/schema artifact (data.page wins over artifact.page). */
export function resolveKioskArtifactPage(a: ChatArtifact): number | null {
  const p = (a.data as any)?.page ?? a.page;
  return typeof p === "number" && p >= 1 ? p : null;
}

/** The URL to FETCH the artifact's image, or null when unresolvable.
 *  - diagram_overlay / cross_reference WITH a page → the absolute page-image
 *    endpoint `${apiBaseUrl}/v1/bot/diagram/{page}/image` (fetched directly by
 *    chatBotApi.fetchBotImage's resolved-endpoint branch).
 *  - plain image / platform_file → the relative bot-image path (proxied via
 *    `/v1/bot/image/`). */
export function resolveKioskArtifactImageUrl(a: ChatArtifact): string | null {
  const page = resolveKioskArtifactPage(a);
  if ((a.type === "diagram_overlay" || a.source === "cross_reference") && page != null) {
    const baseUrl = (window as any).app?.config?.apiBaseUrl || "";
    return `${baseUrl}/v1/bot/diagram/${page}/image`;
  }
  if (a.url && a.url.startsWith("/")) return a.url;
  return null;
}
